import { getDefaultMRTOptions } from '@/modules/common/lib/react-material-table';
import { PaymentStatus } from '@/types/payment';
import { IPaymentGroup, PaymentGroupStage } from '@/types/payment-groups';
import { Paper, Stack, Typography } from '@mui/material';
import { ColumnFiltersState } from '@tanstack/react-table';
import {
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { FilterToolbar } from '../../components/FilterToolbar';
import { PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER } from '../../constants/payment-group-stage';
import { PaymentPageStage } from '../../types';
import { createPaymentGroupsTableColumns } from './ListPaymentGroupsTable.columns';
import { PaymentGroupDetailPanel } from './PaymentGroupDetailPanel';

const defaultMRTOptions = getDefaultMRTOptions<IPaymentGroup>();

type ListPaymentGroupsTableProps = {
  tableOptions: Omit<MRT_TableOptions<IPaymentGroup>, 'columns'>;
  onRetryPayment: (paymentGroup: IPaymentGroup) => void;
  onExport?: () => void;
  isExporting?: boolean;
  stage?: PaymentPageStage;
  hideActions?: boolean;
};

export function ListPaymentGroupsTable({
  tableOptions,
  onRetryPayment,
  onExport,
  isExporting = false,
  stage,
  hideActions,
}: ListPaymentGroupsTableProps) {
  const getAllowedStatuses = (
    selectedStage?: PaymentPageStage,
  ): PaymentStatus[] => {
    if (!selectedStage || selectedStage === PaymentPageStage.ALL) {
      return Object.values(PaymentStatus);
    }

    const stageMapping: Record<PaymentPageStage, PaymentGroupStage> = {
      [PaymentPageStage.ALL]: PaymentGroupStage.AWAITING_PAYMENT, // This won't be used due to the check above
      [PaymentPageStage.AWAITING_PAYMENT]: PaymentGroupStage.AWAITING_PAYMENT,
      [PaymentPageStage.IN_PROGRESS]: PaymentGroupStage.IN_PROGRESS,
      [PaymentPageStage.FAILED]: PaymentGroupStage.FAILED,
      [PaymentPageStage.COMPLETED]: PaymentGroupStage.COMPLETED,
    };

    const groupStage = stageMapping[selectedStage];
    return PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER[groupStage] || [];
  };

  const allowedStatuses = getAllowedStatuses(stage);

  const table = useMaterialReactTable<IPaymentGroup>({
    ...defaultMRTOptions,
    columns: createPaymentGroupsTableColumns(onRetryPayment, hideActions),
    muiTableHeadProps: {
      sx: {
        display: tableOptions.data.length ? undefined : 'none',
      },
    },
    enableTopToolbar: true,
    renderToolbarInternalActions: () => {
      return <></>;
    },
    renderTopToolbar: function TopBar({ table }) {
      const { columnFilters } = table.getState();
      const { setColumnFilters } = table;
      const numberOfRows = table.getRowCount();

      const showClearFilters = columnFilters.some(columnFilter =>
        Array.isArray(columnFilter.value)
          ? columnFilter.value.length
          : columnFilter.value,
      );

      if (!numberOfRows && !showClearFilters) {
        return <div className="empty:hidden"></div>;
      }

      const handleColumnFiltersChange = (filters: ColumnFiltersState) => {
        setColumnFilters(filters);
      };

      return (
        <FilterToolbar
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          allowedStatuses={allowedStatuses}
          onExport={onExport}
          isExporting={isExporting}
          showClearFilters={showClearFilters}
          onClearFilters={() => {
            handleColumnFiltersChange([]);
          }}
          hideEducationProviderFilter
        />
      );
    },
    renderEmptyRowsFallback: () => (
      <div className="flex flex-col justify-center p-3 h-full rounded-lg items-center align-center bg-light-gray text-mydra-black">
        <Paper elevation={0} className="p-4 max-w-[335px]">
          <img
            src="/images/no-results.png"
            alt="No results found"
            width={335}
            height={161}
          />
          <Stack gap={1} className="mt-4">
            <Typography variant="h4" textAlign="center">
              No results found
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              We couldn't find what you're looking for
            </Typography>
          </Stack>
        </Paper>
      </div>
    ),
    muiDetailPanelProps: {
      classes: {
        root: 'px-10 bg-slate-50',
      },
      sx: {
        '& .MuiCollapse-root ': {
          width: '100%',
        },
      },
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 0,
        minSize: 0,
        maxSize: 0,
        header: '',
        Header: () => null,
        Cell: () => null,
        muiTableHeadCellProps: {
          classes: {
            root: '!hidden',
          },
        },
        muiTableBodyCellProps: {
          classes: {
            root: '!hidden',
          },
        },
      },
    },
    renderDetailPanel: ({ row: { original: paymentGroup } }) => (
      <PaymentGroupDetailPanel paymentGroup={paymentGroup} />
    ),
    enablePagination: !!tableOptions?.rowCount,
    ...tableOptions,
  });

  return <MaterialReactTable table={table} />;
}
